/**
 * Status — the most important component in the system.
 *
 * Colour and shape, never colour alone. A red dot and a green dot are the same
 * glyph to roughly 8% of men, and the shape is also what survives a greyscale
 * screenshot pasted into a support ticket. The palette comes from the
 * data-status attribute in components.css; this file only decides which mark
 * and which words go with each state.
 */

import type { MessageStatus, Severity } from "../api/types";

/* ---------------------------------------------------------------------------
   Two maps, and they are allowed to differ for exactly one reason: size.

   SHAPE is drawn by the analytics chart as a data-point marker at 8px.
   status-shapes.svg says its nine marks are "legible in greyscale and at 8px",
   and that constraint is why they are abstract — a paper plane at 8px is a
   smudge, and the greyscale test would fail on the failure family.

   ICON is drawn by a badge at 14px, where a pictogram can say what it means
   without being learned first.

   They sit adjacent deliberately. components.css warns that "two lists of the
   same nine colours would drift the first time one is edited" — that warning is
   about the *colour* mapping, which is still single and still lives in CSS.
   These two are the glyph mapping, and keeping them in one file under one
   comment is what stops them becoming the drift that warning describes.
   --------------------------------------------------------------------------- */

/**
 * Chart markers. Shape families carry meaning: circle is the delivery pipeline,
 * square is recipient engagement, angular is something going wrong.
 *
 * Exported because LineChart draws these — identity is carried by shape there
 * for the same reason it is here.
 */
export const SHAPE: Record<MessageStatus, string> = {
  queued: "oc-shape-ring",
  sent: "oc-shape-half",
  delivered: "oc-shape-dot",
  opened: "oc-shape-square-open",
  clicked: "oc-shape-square-fill",
  bounced: "oc-shape-triangle",
  complained: "oc-shape-cross",
  failed: "oc-shape-diamond",
  unsubscribed: "oc-shape-slash",
};

/** Badge glyphs. Semantic, because at 14px they can afford to be. */
export const ICON: Record<MessageStatus, string> = {
  queued: "oc-status-clock",
  sent: "oc-status-send",
  delivered: "oc-status-check-circle",
  opened: "oc-status-eye",
  clicked: "oc-status-cursor",
  bounced: "oc-status-alert-circle",
  complained: "oc-status-shield-alert",
  failed: "oc-status-x-circle",
  unsubscribed: "oc-status-minus-circle",
};

const LABEL: Record<MessageStatus, string> = {
  queued: "Queued",
  sent: "Sent",
  delivered: "Delivered",
  opened: "Opened",
  clicked: "Clicked",
  bounced: "Bounced",
  complained: "Complained",
  failed: "Failed",
  unsubscribed: "Unsubscribed",
};

/**
 * A bounce reads differently depending on severity, and the difference matters
 * operationally: a soft bounce is retryable and shown amber, a hard bounce is
 * terminal and red. Without a severity we say only "Bounced" rather than
 * guessing — claiming an address is permanently dead when nobody said so is how
 * a live customer gets suppressed.
 */
function labelFor(status: MessageStatus, severity?: Severity): string {
  if (status !== "bounced") return LABEL[status];
  if (severity === "soft") return "Soft bounce";
  if (severity === "hard") return "Hard bounce";
  return "Bounced";
}

interface StatusProps {
  status: MessageStatus;
  severity?: Severity;
}

/**
 * Icon and word, no chip.
 *
 * The chip it replaced was a bordered, tinted, uppercase pill. In a table where
 * every row carries one, that is a column of boxes competing with the content
 * beside it — and the box was never the signal, the glyph and the colour were.
 */
export function Status({ status, severity }: StatusProps) {
  return (
    <span
      className="oc-status"
      data-status={status}
      data-severity={severity === "soft" ? "soft" : undefined}
    >
      <svg className="oc-status__glyph" aria-hidden="true" focusable="false">
        <use href={`#${ICON[status]}`} />
      </svg>
      {labelFor(status, severity)}
    </span>
  );
}

/**
 * The bare glyph, for timelines and dense lists where a word would repeat what
 * the row already says.
 *
 * aria-hidden is correct *only* where adjacent text names the state — which is
 * the case at both call sites, since .oc-timeline__label carries the status
 * name. Used anywhere a label does not, this needs role="img" and an
 * aria-label, or the state becomes invisible to a screen reader.
 */
export function StatusGlyph({ status }: { status: MessageStatus }) {
  return (
    <svg className="oc-status-glyph" data-status={status} aria-hidden="true" focusable="false">
      <use href={`#${ICON[status]}`} />
    </svg>
  );
}

export { labelFor as statusLabel };
